<template>
    <div class="application-agreement">
        <div class="application-agreement-check">
            <input
                ref="checkbox"
                type="checkbox"
                :checked="state.accepted"
                :disabled="disabled"
                aria-label="我已阅读并同意本专业协议"
                @change="toggleCheckbox"
            />
            <span
                >我已阅读并同意
                <button
                    type="button"
                    :disabled="disabled"
                    @click="$emit('read')"
                >
                    《{{ title }}》
                </button></span
            >
        </div>
        <div
            v-if="state.reader || state.reminder"
            class="agreement-overlay"
            @keydown.esc.prevent="$emit(state.reader ? 'exit' : 'back')"
            @keydown.tab="trapFocus"
        >
            <section
                v-if="state.reader"
                ref="modal"
                class="agreement-reader"
                role="dialog"
                aria-modal="true"
                aria-labelledby="applicationAgreementTitle"
                tabindex="-1"
            >
                <header>
                    <h2 id="applicationAgreementTitle">{{ title }}</h2>
                    <button
                        type="button"
                        aria-label="关闭协议阅读"
                        @click="$emit('exit')"
                    >
                        ×
                    </button>
                </header>
                <main>
                    <p v-if="state.loading" role="status">
                        正在加载本专业正式协议…
                    </p>
                    <div
                        v-else-if="state.error"
                        role="alert"
                        class="agreement-error"
                    >
                        <p>{{ state.error }}</p>
                        <button type="button" @click="$emit('retry')">
                            重新加载协议
                        </button>
                    </div>
                    <template v-else-if="state.ready"
                        ><p class="agreement-version">
                            协议版本：{{ state.version }}
                        </p>
                        <div class="agreement-body">
                            {{ state.content }}
                        </div></template
                    >
                </main>
                <footer>
                    <p
                        role="status"
                        :class="{ ready: state.ready && state.remaining === 0 }"
                    >
                        {{
                            !state.ready
                                ? "请先成功加载本专业正式协议"
                                : state.remaining > 0
                                ? "请阅读协议，10秒后可点击同意"
                                : "倒计时已结束，可点击同意并继续"
                        }}
                    </p>
                    <button
                        type="button"
                        class="agreement-primary"
                        :disabled="
                            !state.ready ||
                            state.loading ||
                            !!state.error ||
                            state.remaining > 0
                        "
                        @click="$emit('agree')"
                    >
                        同意并继续{{
                            state.ready && state.remaining > 0
                                ? "（" + state.remaining + "s）"
                                : ""
                        }}
                    </button>
                    <button
                        type="button"
                        class="agreement-exit"
                        @click="$emit('exit')"
                    >
                        不同意，退出
                    </button>
                </footer>
            </section>
            <section
                v-else
                ref="modal"
                class="agreement-reminder"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="agreementReminderTitle"
                aria-describedby="agreementReminderCopy"
                tabindex="-1"
            >
                <h2 id="agreementReminderTitle">请先勾选协议</h2>
                <p id="agreementReminderCopy">
                    请阅读并勾选《{{ title }}》后，再提交首次申请。
                </p>
                <button
                    type="button"
                    class="agreement-primary"
                    @click="$emit('back')"
                >
                    返回勾选
                </button>
            </section>
        </div>
    </div>
</template>
<script>
export default {
    name: "ApplicationAgreementPanel",
    props: {
        state: { type: Object, required: true },
        title: String,
        disabled: Boolean,
    },
    watch: {
        "state.reader": "focusModal",
        "state.reminder": "focusModal",
    },
    methods: {
        toggleCheckbox(event) {
            event.target.checked = this.state.accepted;
            this.$emit("toggle");
        },
        focusCheckbox() {
            this.$refs.checkbox.focus();
        },
        focusModal() {
            this.$nextTick(() => {
                if (this.$refs.modal) this.$refs.modal.focus();
            });
        },
        trapFocus(event) {
            const elements = this.$refs.modal.querySelectorAll(
                "button:not(:disabled)"
            );
            const first = elements[0],
                last = elements[elements.length - 1];
            if (
                event.shiftKey &&
                (document.activeElement === first ||
                    document.activeElement === this.$refs.modal)
            ) {
                event.preventDefault();
                last.focus();
            } else if (
                !event.shiftKey &&
                (document.activeElement === last ||
                    document.activeElement === this.$refs.modal)
            ) {
                event.preventDefault();
                first.focus();
            }
        },
    },
};
</script>
<style scoped>
.application-agreement {
    margin-top: 24px;
    color: #1c1c1e;
    font-size: 13px;
}
.application-agreement-check {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    line-height: 24px;
}
.application-agreement-check input {
    width: 18px;
    height: 18px;
    margin: 3px 0;
    accent-color: #7c6ff0;
    flex-shrink: 0;
}
.application-agreement-check button {
    padding: 0;
    color: #7c6ff0;
    background: transparent;
    border: 0;
    font: inherit;
    cursor: pointer;
    text-align: left;
}
.agreement-overlay {
    position: fixed;
    inset: 0;
    z-index: 100;
    display: grid;
    place-items: center;
    padding: 24px;
    background: rgba(33, 27, 73, 0.35);
}
.agreement-reader {
    width: min(720px, 100%);
    height: min(820px, calc(100vh - 48px));
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #f7f7fb;
    border-radius: 20px;
    color: #656576;
}
.agreement-reader header {
    display: flex;
    align-items: center;
    padding: 24px;
    border-bottom: 1px solid #eaeaf2;
    gap: 12px;
}
.agreement-reader h2 {
    flex: 1;
    margin: 0;
    color: #1c1c1e;
    font-size: 18px;
    line-height: 1.5;
}
.agreement-reader header button {
    width: 44px;
    height: 44px;
    color: #656576;
    background: transparent;
    border: 0;
    font-size: 26px;
    cursor: pointer;
}
.agreement-reader main {
    min-height: 0;
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    font-size: 14px;
    line-height: 1.8;
}
.agreement-body {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}
.agreement-version {
    margin: 0 0 16px;
    font-size: 12px;
}
.agreement-reader footer {
    padding: 16px 24px 24px;
    border-top: 1px solid #eaeaf2;
    text-align: center;
}
.agreement-reader footer p {
    margin: 0 0 12px;
    color: #656576;
    font-size: 12px;
    line-height: 18px;
}
.agreement-reader footer p.ready {
    color: #7c6ff0;
}
.agreement-primary {
    width: 100%;
    min-height: 48px;
    padding: 12px 16px;
    border: 0;
    border-radius: 12px;
    background: #7c6ff0;
    color: white;
    font: inherit;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
}
.agreement-primary:disabled {
    background: #e8e5fb;
    color: #777098;
    cursor: not-allowed;
}
.agreement-exit {
    display: block;
    width: 100%;
    min-height: 44px;
    margin: 8px 0 -8px;
    color: #656576;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 13px;
    cursor: pointer;
}
.agreement-reminder {
    width: min(375px, 100%);
    padding: 32px 24px 24px;
    border-radius: 24px;
    background: white;
    box-shadow: 0 16px 40px #211b4926;
    text-align: center;
}
.agreement-reminder h2 {
    margin: 0 0 16px;
    font-size: 18px;
    line-height: 24px;
}
.agreement-reminder p {
    margin: 0 0 24px;
    color: #656576;
    font-size: 14px;
    line-height: 24px;
}
.agreement-error button {
    min-height: 44px;
    padding: 8px 16px;
    color: #6254cf;
    border: 1px solid #b9b0fb;
    border-radius: 10px;
    background: white;
    font: inherit;
    cursor: pointer;
}
.application-agreement button:focus-visible,
.application-agreement input:focus-visible,
.agreement-reader:focus-visible,
.agreement-reminder:focus-visible {
    outline: 3px solid #5444cc !important;
    outline-offset: 3px;
}
@media (max-width: 760px) {
    .agreement-overlay {
        padding: 12px;
    }
    .agreement-reader {
        width: 100%;
        height: calc(100vh - 24px);
        border-radius: 16px;
    }
    .agreement-reader header {
        padding: 16px;
    }
    .agreement-reader h2 {
        font-size: 16px;
    }
    .agreement-reader main {
        padding: 24px;
    }
}
</style>
